import { type JSX } from "react";
import { useTranslation } from "react-i18next";

import { statusIntent, statusLabel } from "@/design";
import { Badge } from "@/shared/ui/badge";
import { Drawer } from "@/shared/ui/drawer";
import { Skeleton } from "@/shared/ui/skeleton";

import { usePushLog } from "../api/use-push";
import { relativeSyncTime } from "../lib/format-sync-time";

interface PushHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  sku: string;
  mkp: string;
}

export function PushHistoryDrawer({
  open,
  onClose,
  sku,
  mkp,
}: PushHistoryDrawerProps): JSX.Element {
  const { t, i18n } = useTranslation("inventory");
  const { data, isLoading } = usePushLog(sku, mkp, open);
  const items = data?.items ?? [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={<span data-testid="push-history-title">{t("push.history.title", { sku, mkp })}</span>}
      width={420}
    >
      {isLoading ? (
        <div className="space-y-3 px-5 py-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-[var(--text-subtle)]">
          {t("push.history.empty")}
        </p>
      ) : (
        <ul data-testid="push-history-list" className="divide-y divide-[var(--row-divider)] px-5">
          {items.map((entry, i) => (
            <li
              key={`${entry.at}-${i}`}
              data-testid="push-history-row"
              className="flex items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-[var(--text-primary)]">
                  {entry.result === "ok"
                    ? t("push.history.sent", {
                        qty: entry.qty_sent,
                        alloc: entry.qty_allocation,
                        reserved: entry.qty_reserved_added,
                      })
                    : entry.result === "failed" && entry.error
                      ? t("push.history.failed", { error: entry.error })
                      : t(`push.history.result.${entry.result}`)}
                </p>
                <p className="text-xs text-[var(--text-subtle)]">
                  <time dateTime={entry.at} title={entry.at}>
                    {relativeSyncTime(entry.at, i18n.language)}
                  </time>
                  {" · "}
                  {t(`push.history.reason.${entry.reason}`)}
                  {entry.actor && entry.actor !== "system" ? ` · ${entry.actor}` : ""}
                </p>
              </div>
              <Badge intent={statusIntent("push_result", entry.result)}>
                {statusLabel("push_result", entry.result, i18n.language)}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}
